import React from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { ThemedText, EmptyState } from '../components';
import { Database, FileText, FolderTree } from 'lucide-react';
import KnowledgeChatView from './organization/KnowledgeChatView';
import KnowledgeDocumentsView from './organization/KnowledgeDocumentsView';
import KnowledgeTopicsView from './organization/KnowledgeTopicsView';

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Subtitle = styled.span`
  color: ${(p) => p.theme.colors.muted};
  font-size: 13px;
`;

const SubTabBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: ${(p) => p.theme.colors.backgroundSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.md};
  margin-bottom: ${(p) => p.theme.spacing.lg};
  width: fit-content;
`;

const SubTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${(p) => (p.$active ? p.theme.colors.background : 'transparent')};
  border: none;
  border-radius: ${(p) => p.theme.borderRadius.sm};
  color: ${(p) => (p.$active ? p.theme.colors.text : p.theme.colors.muted)};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${(p) => p.theme.colors.text};
  }
`;

// Top-level /knowledge-base page — Chat only, for everyone in the org.
// Document/topic/sync management lives in Settings → Knowledge Base (admin-only).
export const KnowledgeBasePage = () => {
  const theme = useTheme();
  const { getOrganizationId } = useRole();
  const organizationId = getOrganizationId();

  return (
    <Layout currentPath="/knowledge-base">
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <HeaderText>
            <ThemedText type="title">Knowledge Base</ThemedText>
            <Subtitle theme={theme}>
              Ask questions across your organization's documents. Admins manage documents and topics under Settings → Knowledge Base.
            </Subtitle>
          </HeaderText>
        </PageHeader>

        {organizationId ? (
          <KnowledgeChatView organizationId={organizationId} theme={theme} />
        ) : (
          <EmptyState
            icon={<Database size={32} />}
            title="No organization selected"
            description="Super admins should pick an organization from the Organizations page to use its knowledge base."
          />
        )}
      </PageContainer>
    </Layout>
  );
};

export default KnowledgeBasePage;

// Admin-only Knowledge Base management (Documents + Topics + Sync), embedded as a
// Settings tab. Inner sub-tab lives in `?sub=` so it preserves the host `?tab=`.
export const KnowledgeBaseSettingsSection = () => {
  const theme = useTheme();
  const { getOrganizationId } = useRole();
  const organizationId = getOrganizationId();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = searchParams.get('sub') === 'topics' ? 'topics' : 'documents';
  const setSub = (s) =>
    setSearchParams((prev) => {
      prev.set('sub', s);
      return prev;
    });

  if (!organizationId) {
    return (
      <EmptyState
        icon={<Database size={32} />}
        title="No organization selected"
        description="Super admins manage a specific organization's knowledge base from the Organizations page."
      />
    );
  }

  return (
    <div>
      <SubTabBar theme={theme}>
        <SubTab theme={theme} $active={sub === 'documents'} onClick={() => setSub('documents')}>
          <FileText size={14} /> Documents
        </SubTab>
        <SubTab theme={theme} $active={sub === 'topics'} onClick={() => setSub('topics')}>
          <FolderTree size={14} /> Topics
        </SubTab>
      </SubTabBar>

      {sub === 'documents' && (
        <KnowledgeDocumentsView organizationId={organizationId} canManage theme={theme} />
      )}
      {sub === 'topics' && (
        <KnowledgeTopicsView organizationId={organizationId} canManage theme={theme} />
      )}
    </div>
  );
};
